import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { regionalInPersonAPI, reportsAPI } from '../services/api';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import { ContentItemLinkDialog } from '../components/content-items/ContentItemLinkDialog';
import {
  ArrowLeft, Edit2, Trash2, Check, X, Upload, Loader2,
  MapPin, Calendar, Users, Star, Tent, Image, Plus, Clock,
  CheckCircle2, Circle, AlertTriangle, Mail, Download, Layers,
} from 'lucide-react';
import { toast } from 'sonner';
import { format, differenceInDays, parseISO, isValid } from 'date-fns';

const DELIVERY_STATUS_STYLES = {
  Scheduled: 'bg-blue-100 text-blue-800',
  Delivered: 'bg-green-100 text-green-800',
  Cancelled: 'bg-red-100 text-red-800',
  Postponed: 'bg-yellow-100 text-yellow-800',
  'Converted to Virtual': 'bg-purple-100 text-purple-800',
};

const STATUS_OPTIONS = ['Scheduled', 'Delivered', 'Cancelled', 'Postponed', 'Converted to Virtual'];
const WORKLOADS = ['Security', 'Agentic AI', 'Copilot', 'Fabric', 'Modernize Apps', 'Sovereign Cloud', 'Other'];
const REGIONS = ['Asia', 'Americas', 'EMEA', 'MEA', 'Other'];
const LANGUAGES = ['English', 'Spanish', 'Portuguese', 'French', 'German', 'Italian', 'Japanese', 'Korean', 'Chinese (Simplified)', 'Chinese (Traditional)', 'Turkish', 'Arabic', 'Other'];
const POE_OPTIONS = ['Yes', 'No', 'Pending'];
const COURSE_TYPES = ['TDR', 'PR', 'TDR+PR'];

const fmt = (d) => { try { return d ? format(new Date(d), 'MMM d, yyyy') : '—'; } catch { return d || '—'; } };

function DaysLeft({ dateStr }) {
  if (!dateStr) return null;
  try {
    const d = parseISO(dateStr);
    if (!isValid(d)) return null;
    const days = differenceInDays(d, new Date());
    if (days < 0) return <span className="text-xs text-muted-foreground">Event passed</span>;
    const color = days < 7 ? 'text-red-600 bg-red-50' : days < 21 ? 'text-yellow-700 bg-yellow-50' : 'text-green-700 bg-green-50';
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${color}`}>
        <Clock className="w-3 h-3" />{days} days left
      </span>
    );
  } catch { return null; }
}

function FieldRow({ label, value, editComp, editing }) {
  return (
    <div className="flex items-start gap-3 py-2 border-b last:border-0">
      <span className="text-xs text-muted-foreground w-36 flex-shrink-0 pt-1">{label}</span>
      {editing ? editComp : <span className="text-sm">{value || <span className="text-muted-foreground italic">—</span>}</span>}
    </div>
  );
}

const TABS = ['Event Details', 'Trainer Details', 'Instructor Resources', 'Connect Calls'];

export const RegionalInPersonDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [highlight, setHighlight] = useState(!!location.state?.highlight);
  useEffect(() => {
    if (!highlight) return;
    const t = setTimeout(() => setHighlight(false), 2500);
    return () => clearTimeout(t);
  }, [highlight]);
  const [event, setEvent] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(0);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [outcomesEdit, setOutcomesEdit] = useState(false);
  const [outcomes, setOutcomes] = useState({});
  const [savingOutcomes, setSavingOutcomes] = useState(false);
  const fileInputRef = useRef();
  // Report modal
  const [reportOpen, setReportOpen] = useState(false);
  const [reportLoading, setReportLoading] = useState(false);
  const [reportHtml, setReportHtml] = useState('');
  const [contentItemDialogOpen, setContentItemDialogOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const [evtData, photosData] = await Promise.all([
        regionalInPersonAPI.getById(id),
        regionalInPersonAPI.getPhotos(id),
      ]);
      setEvent(evtData);
      setPhotos(photosData);
      setForm({ ...evtData });
      setOutcomes({
        total_attendance: evtData.total_attendance ?? '',
        unique_partners: evtData.unique_partners ?? '',
        feedback_score: evtData.feedback_score ?? '',
        session_feedback: evtData.session_feedback ?? '',
        trainer_feedback: evtData.trainer_feedback ?? '',
      });
    } catch { toast.error('Failed to load event'); }
    finally { setLoading(false); }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const set = (field, value) => setForm(p => ({ ...p, [field]: value }));

  const handleSave = async () => {
    setSaving(true);
    try {
      const updated = await regionalInPersonAPI.update(id, form);
      setEvent(updated);
      setForm({ ...updated });
      setEditing(false);
      toast.success('Changes saved');
    } catch { toast.error('Failed to save'); }
    finally { setSaving(false); }
  };

  const handleCancelEdit = () => { setForm({ ...event }); setEditing(false); };

  const handleUnlinkContentItem = async () => {
    if (!window.confirm('Remove the linked Content Dev course from this event?')) return;
    try {
      const updated = await regionalInPersonAPI.update(id, { content_item_id: null, content_item_title: null });
      setEvent(updated);
      setForm(prev => ({ ...prev, content_item_id: null, content_item_title: null }));
      toast.success('Content Dev link removed');
    } catch { toast.error('Failed to unlink'); }
  };

  const handleLinkContentItem = async (item) => {
    setContentItemDialogOpen(false);
    try {
      const updated = await regionalInPersonAPI.update(id, { content_item_id: item?.id || null, content_item_title: item?.title || null });
      setEvent(updated);
      setForm(prev => ({ ...prev, content_item_id: item?.id || null, content_item_title: item?.title || null }));
      toast.success('Linked to Content Dev course');
    } catch { toast.error('Failed to link'); }
  };

  const saveOutcomes = async () => {
    setSavingOutcomes(true);
    try {
      const payload = {
        total_attendance: outcomes.total_attendance !== '' ? parseInt(outcomes.total_attendance) : null,
        unique_partners: outcomes.unique_partners !== '' ? parseInt(outcomes.unique_partners) : null,
        feedback_score: outcomes.feedback_score !== '' ? parseFloat(outcomes.feedback_score) : null,
        session_feedback: outcomes.session_feedback || null,
        trainer_feedback: outcomes.trainer_feedback || null,
      };
      const updated = await regionalInPersonAPI.update(id, payload);
      setEvent(updated);
      setOutcomesEdit(false);
      toast.success('Outcomes saved');
    } catch { toast.error('Failed to save outcomes'); }
    finally { setSavingOutcomes(false); }
  };

  const handlePhotoUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    for (const file of files) {
      try {
        await regionalInPersonAPI.uploadPhoto(id, file);
        toast.success(`Uploaded ${file.name}`);
      } catch { toast.error(`Failed to upload ${file.name}`); }
    }
    load();
    e.target.value = '';
  };

  const handleDeletePhoto = async (photoId) => {
    try {
      await regionalInPersonAPI.deletePhoto(photoId);
      setPhotos(p => p.filter(ph => ph.id !== photoId));
      toast.success('Photo removed');
    } catch { toast.error('Failed to remove photo'); }
  };

  const handleGenerateReport = async () => {
    setReportOpen(true);
    setReportLoading(true);
    setReportHtml('');
    try {
      const result = await reportsAPI.generateRIOReport(id);
      setReportHtml(result.html);
    } catch { toast.error('Report generation failed'); setReportOpen(false); }
    finally { setReportLoading(false); }
  };

  const downloadReportHtml = () => {
    const blob = new Blob([reportHtml], { type: 'text/html;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${(event?.title || 'report').replace(/\s+/g, '_')}_report.html`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="w-8 h-8 animate-spin text-lime" /></div>;
  if (!event) return <div className="text-center py-16 text-muted-foreground">Event not found</div>;

  const f = editing ? form : event;

  return (
    <>
    <div className="space-y-6 animate-fade-in max-w-5xl" data-testid="rio-detail-page">
      <div className={`flex flex-col sm:flex-row sm:items-start gap-4 rounded-lg -m-2 p-2 transition-colors duration-1000 ${highlight ? 'bg-lime/20' : ''}`} data-testid="rio-detail-header">
        <Button variant="ghost" size="icon" onClick={() => navigate('/regional-in-person')} className="self-start">
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2 mb-1">
            <Badge className={DELIVERY_STATUS_STYLES[event.delivery_status] || 'bg-gray-100 text-gray-700'}>
              {event.delivery_status || 'Scheduled'}
            </Badge>
            {event.region && <Badge variant="outline">{event.region}</Badge>}
            {event.course_type && <Badge variant="outline">{event.course_type}</Badge>}
            {event.delivery_days && <Badge variant="outline">{event.delivery_days}-Day</Badge>}
            {event.preferred_dates && <DaysLeft dateStr={event.preferred_dates} />}
          </div>
          <h1 className="text-xl font-heading font-bold break-words">{event.title}</h1>
          <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1 text-sm text-muted-foreground">
            {(event.city || event.country) && (
              <span className="flex items-center gap-1"><MapPin className="w-3.5 h-3.5" />{[event.city, event.country].filter(Boolean).join(', ')}</span>
            )}
            {event.preferred_dates && <span className="flex items-center gap-1"><Calendar className="w-3.5 h-3.5" />{fmt(event.preferred_dates)}{event.training_end_date ? ` – ${fmt(event.training_end_date)}` : ''}</span>}
            {event.workload && <span>{event.workload}</span>}
            {event.po_number && <span className="font-mono text-xs">PO: {event.po_number}</span>}
          </div>
        </div>
        <div className="flex gap-2 flex-shrink-0">
          {editing ? (
            <>
              <Button size="sm" onClick={handleSave} disabled={saving} className="bg-lime text-lime-foreground hover:bg-lime/90">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
              </Button>
              <Button size="sm" variant="outline" onClick={handleCancelEdit}><X className="w-4 h-4" /></Button>
            </>
          ) : (
            <>
              <Button variant="outline" size="sm" onClick={handleGenerateReport}
                data-testid="rio-generate-report-btn" className="hidden sm:flex">
                <Mail className="w-4 h-4 mr-1.5" />Generate Report
              </Button>
              <Button variant="outline" size="sm" onClick={() => setEditing(true)} data-testid="rio-edit-btn">
                <Edit2 className="w-4 h-4 mr-1.5" />Edit
              </Button>
              <Button variant="outline" size="sm" className="text-destructive border-destructive/30 hover:bg-destructive/10"
                onClick={async () => { if (!window.confirm('Delete this event?')) return; await regionalInPersonAPI.delete(id); navigate('/regional-in-person'); }}>
                <Trash2 className="w-4 h-4" />
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Tab bar */}
      <div className="flex gap-0 border-b">
        {TABS.map((t, i) => (
          <button key={t} onClick={() => setActiveTab(i)}
            className={`px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors ${
              activeTab === i ? 'border-lime text-foreground' : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}>{t}</button>
        ))}
      </div>

      {/* Tab 1: Event Details */}
      {activeTab === 0 && (
        <Card>
          <CardContent className="pt-5">
            <div className="grid sm:grid-cols-2 gap-x-8">
              <div>
                {[
                  { label: 'Start Date', field: 'preferred_dates', type: 'date', value: fmt(event.preferred_dates) },
                  { label: 'End Date', field: 'training_end_date', type: 'date', value: fmt(event.training_end_date) },
                  { label: 'Delivery Days', field: 'delivery_days', type: 'select', options: ['1','2'], value: String(event.delivery_days ?? '—') },
                  { label: 'Course Type', field: 'course_type', type: 'select', options: COURSE_TYPES, value: event.course_type },
                  { label: 'Workload', field: 'workload', type: 'select', options: WORKLOADS, value: event.workload },
                  { label: 'Delivery Language', field: 'delivery_language', type: 'select', options: LANGUAGES, value: event.delivery_language },
                ].map(({ label, field, type, options, value }) => (
                  <FieldRow key={field} label={label} value={value} editing={editing} editComp={
                    type === 'select' ? (
                      <Select value={f[field] ? String(f[field]) : ''} onValueChange={v => set(field, field === 'delivery_days' ? parseInt(v) : v)}>
                        <SelectTrigger className="h-7 text-sm"><SelectValue /></SelectTrigger>
                        <SelectContent>{(options || []).map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
                      </Select>
                    ) : (
                      <Input type={type} value={f[field] || ''} onChange={e => set(field, e.target.value)} className="h-7 text-sm" />
                    )
                  } />
                ))}
              </div>
              <div>
                {[
                  { label: 'Content+Labs Lang', field: 'content_labs_language', type: 'select', options: LANGUAGES, value: event.content_labs_language },
                  { label: 'City', field: 'city', type: 'text', value: event.city },
                  { label: 'Country', field: 'country', type: 'text', value: event.country },
                  { label: 'Region', field: 'region', type: 'select', options: REGIONS, value: event.region },
                  { label: 'PO Number', field: 'po_number', type: 'text', value: event.po_number },
                  { label: 'Delivery Status', field: 'delivery_status', type: 'select', options: STATUS_OPTIONS, value: event.delivery_status },
                  { label: 'POE Shared', field: 'poe_shared', type: 'select', options: POE_OPTIONS, value: event.poe_shared },
                ].map(({ label, field, type, options, value }) => (
                  <FieldRow key={field} label={label} value={value} editing={editing} editComp={
                    type === 'select' ? (
                      <Select value={f[field] || ''} onValueChange={v => set(field, v)}>
                        <SelectTrigger className="h-7 text-sm"><SelectValue /></SelectTrigger>
                        <SelectContent>{(options || []).map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
                      </Select>
                    ) : (
                      <Input type={type} value={f[field] || ''} onChange={e => set(field, e.target.value)} className="h-7 text-sm" />
                    )
                  } />
                ))}
              </div>
            </div>
            {editing && (
              <div className="mt-4 space-y-3">
                <div>
                  <Label className="text-xs">Prospective Trainers</Label>
                  <Textarea value={f.prospective_trainers || ''} onChange={e => set('prospective_trainers', e.target.value)} rows={2} className="mt-1 text-sm" />
                </div>
                <div>
                  <Label className="text-xs">Declined Trainers</Label>
                  <Textarea value={f.declined_trainers || ''} onChange={e => set('declined_trainers', e.target.value)} rows={2} className="mt-1 text-sm" />
                </div>
              </div>
            )}
            {!editing && (event.prospective_trainers || event.declined_trainers) && (
              <div className="mt-4 grid sm:grid-cols-2 gap-4">
                {event.prospective_trainers && (
                  <div>
                    <p className="text-xs text-muted-foreground font-medium mb-1">Prospective Trainers</p>
                    <p className="text-sm">{event.prospective_trainers}</p>
                  </div>
                )}
                {event.declined_trainers && (
                  <div>
                    <p className="text-xs text-muted-foreground font-medium mb-1">Declined</p>
                    <p className="text-sm text-muted-foreground">{event.declined_trainers}</p>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Tab 2: Trainer Details + Travel Tracker */}
      {activeTab === 1 && (
        <div className="space-y-4">
          {/* Days Left Banner */}
          {event.preferred_dates && (
            <div className={`flex items-center gap-3 p-4 rounded-lg border ${
              (() => { try { const d = differenceInDays(parseISO(event.preferred_dates), new Date()); return d < 7 ? 'border-red-200 bg-red-50' : d < 21 ? 'border-yellow-200 bg-yellow-50' : 'border-green-200 bg-green-50'; } catch { return 'border-border bg-muted/20'; } })()
            }`}>
              <Clock className="w-5 h-5 flex-shrink-0" />
              <div>
                <p className="text-sm font-medium">Travel Tracker — {fmt(event.preferred_dates)}</p>
                <DaysLeft dateStr={event.preferred_dates} />
              </div>
            </div>
          )}

          <div className="grid sm:grid-cols-2 gap-4">
            {[1, 2].map(n => (
              <Card key={n}>
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-sm">Trainer {n}</CardTitle>
                    {event[`trainer${n}_readiness`] && <Badge className="bg-green-100 text-green-800 text-xs">Ready</Badge>}
                  </div>
                </CardHeader>
                <CardContent className="space-y-2">
                  {[
                    ['name', 'Name'],
                    ['contract_no', 'Contract #'],
                    ['email', 'TF Email'],
                    ['arrival_date', 'Arrival Date'],
                    ['travel_plan', 'Travel Plan'],
                  ].map(([field, label]) => (
                    <FieldRow key={field} label={label}
                      value={field === 'arrival_date' ? fmt(event[`trainer${n}_${field}`]) : event[`trainer${n}_${field}`]}
                      editing={editing}
                      editComp={
                        field === 'travel_plan' ? (
                          <Select value={f[`trainer${n}_${field}`] || ''} onValueChange={v => set(`trainer${n}_${field}`, v)}>
                            <SelectTrigger className="h-7 text-sm"><SelectValue placeholder="Select..." /></SelectTrigger>
                            <SelectContent>{['Shared','Awaited'].map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
                          </Select>
                        ) : (
                          <Input type={field === 'arrival_date' ? 'date' : 'text'} value={f[`trainer${n}_${field}`] || ''}
                            onChange={e => set(`trainer${n}_${field}`, e.target.value)} className="h-7 text-sm" />
                        )
                      } />
                  ))}

                  {/* Travel Tracker fields */}
                  <div className="pt-2 border-t space-y-2">
                    <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Travel Details</p>
                    <FieldRow label="Flight/Train" value={event[`trainer${n}_flight_details`]} editing={editing}
                      editComp={<Input value={f[`trainer${n}_flight_details`] || ''} onChange={e => set(`trainer${n}_flight_details`, e.target.value)} className="h-7 text-sm" placeholder="e.g. EK 123, Seat 14A" />} />
                    <div className="flex items-center gap-3 py-1.5">
                      <span className="text-xs text-muted-foreground w-36">Internal Call Done</span>
                      <Checkbox
                        checked={editing ? !!f[`trainer${n}_internal_call_done`] : !!event[`trainer${n}_internal_call_done`]}
                        onCheckedChange={v => editing && set(`trainer${n}_internal_call_done`, v)}
                        disabled={!editing}
                      />
                    </div>
                    {editing && (
                      <div className="flex items-center gap-3 py-1.5">
                        <span className="text-xs text-muted-foreground w-36">Trainer Ready</span>
                        <Checkbox checked={!!f[`trainer${n}_readiness`]} onCheckedChange={v => set(`trainer${n}_readiness`, v)} />
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* Tab 3: Instructor Resources */}
      {activeTab === 2 && (
        <div className="space-y-4">
          <Card>
            <CardHeader className="pb-3 flex flex-row items-center justify-between">
              <CardTitle className="text-base">Event Content</CardTitle>
              {event.content_item_id ? (
                <div className="flex items-center gap-2">
                  <button type="button" onClick={() => navigate(`/content-items/${event.content_item_id}`)}
                    className="text-sm text-lime-700 hover:underline flex items-center gap-1.5" data-testid="view-linked-content-item-btn">
                    <Layers className="w-3.5 h-3.5" />{event.content_item_title || 'View Content Dev Course'}
                  </button>
                  <button type="button" onClick={handleUnlinkContentItem} className="text-muted-foreground hover:text-red-600" title="Unlink Content Dev course" data-testid="unlink-content-item-btn">
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
              ) : (
                <Button variant="outline" size="sm" onClick={() => setContentItemDialogOpen(true)} className="gap-1.5 h-7 text-xs" data-testid="link-content-item-btn">
                  <Layers className="w-3 h-3" />Link Content Dev Course
                </Button>
              )}
            </CardHeader>
          </Card>
        <Card>
          <CardContent className="pt-5 space-y-4">
            <div className="grid sm:grid-cols-2 gap-4">
              {[
                { label: 'Content + Labs Shared', field: 'rio_content_labs_status' },
                { label: 'Lab Key Shared', field: 'rio_lab_key_status' },
              ].map(({ label, field }) => (
                <div key={field}>
                  <p className="text-xs text-muted-foreground mb-1">{label}</p>
                  {editing ? (
                    <Select value={f[field] || 'Pending'} onValueChange={v => set(field, v)}>
                      <SelectTrigger className="h-8 text-sm"><SelectValue /></SelectTrigger>
                      <SelectContent>{['Yes','No','Pending'].map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                    </Select>
                  ) : (
                    <Badge className={event[field] === 'Yes' ? 'bg-green-100 text-green-800' : event[field] === 'No' ? 'bg-red-100 text-red-800' : 'bg-yellow-100 text-yellow-800'}>
                      {event[field] || 'Pending'}
                    </Badge>
                  )}
                </div>
              ))}
            </div>
            <div className="space-y-3 pt-2">
              {[
                ['event_venue_shared', 'Event Venue Details shared with trainers'],
                ['poc_details_shared', 'MS Onsite POC details shared'],
                ['registration_pages_shared', 'Event Registration pages shared'],
              ].map(([field, label]) => (
                <div key={field} className="flex items-center gap-3">
                  {editing ? (
                    <Checkbox checked={!!f[field]} onCheckedChange={v => set(field, v)} />
                  ) : (
                    event[field]
                      ? <CheckCircle2 className="w-4 h-4 text-lime" />
                      : <Circle className="w-4 h-4 text-muted-foreground/40" />
                  )}
                  <span className={`text-sm ${!editing && event[field] ? 'line-through text-muted-foreground' : ''}`}>{label}</span>
                </div>
              ))}
            </div>
            {(editing || event.instructor_remarks) && (
              <div>
                <Label className="text-xs">Remarks / Status</Label>
                {editing ? (
                  <Textarea value={f.instructor_remarks || ''} onChange={e => set('instructor_remarks', e.target.value)} rows={3} className="mt-1 text-sm" />
                ) : (
                  <p className="text-sm mt-1 text-muted-foreground">{event.instructor_remarks}</p>
                )}
              </div>
            )}
          </CardContent>
        </Card>
        </div>
      )}

      {/* Tab 4: Connect Calls */}
      {activeTab === 3 && (
        <Card>
          <CardContent className="pt-5 space-y-6">
            {[
              { label: 'Internal Pre-Execution Call', sublabel: 'Alignment call with both trainers before event', field: 'internal_call_date' },
              { label: 'External MS Team Call', sublabel: 'Call with Microsoft team + trainers (audience, registration, local asks)', field: 'ms_call_date' },
            ].map(({ label, sublabel, field }) => (
              <div key={field}>
                <p className="text-sm font-medium">{label}</p>
                <p className="text-xs text-muted-foreground mb-2">{sublabel}</p>
                {editing ? (
                  <Input type="date" value={f[field] || ''} onChange={e => set(field, e.target.value)} className="max-w-xs" />
                ) : (
                  <div className="flex items-center gap-2">
                    {event[field]
                      ? <><CheckCircle2 className="w-4 h-4 text-lime" /><span className="text-sm font-medium">{fmt(event[field])}</span></>
                      : <><Circle className="w-4 h-4 text-muted-foreground/40" /><span className="text-sm text-muted-foreground italic">Not scheduled</span></>
                    }
                  </div>
                )}
              </div>
            ))}
            {(editing || event.notes) && (
              <div>
                <Label className="text-xs">Notes</Label>
                {editing ? (
                  <Textarea value={f.notes || ''} onChange={e => set('notes', e.target.value)} rows={3} className="mt-1 text-sm" />
                ) : (
                  <p className="text-sm mt-1 text-muted-foreground">{event.notes}</p>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Outcomes Section */}
      <Card>
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base">Event Outcomes</CardTitle>
            {!outcomesEdit && <Button variant="ghost" size="sm" onClick={() => setOutcomesEdit(true)} className="h-7 text-xs" data-testid="rio-edit-outcomes-btn"><Edit2 className="w-3 h-3 mr-1" />Edit</Button>}
          </div>
        </CardHeader>
        <CardContent className="pt-0">
          {outcomesEdit ? (
            <div className="space-y-3">
              <div className="grid sm:grid-cols-3 gap-3">
                {[['total_attendance','Total Attendance','number'],['unique_partners','Unique Partners','number'],['feedback_score','Feedback Score (/5)','number']].map(([field, label, type]) => (
                  <div key={field}>
                    <Label className="text-xs">{label}</Label>
                    <Input type={type} step={field === 'feedback_score' ? '0.1' : '1'} max={field === 'feedback_score' ? '5' : undefined}
                      value={outcomes[field]} onChange={e => setOutcomes(p => ({ ...p, [field]: e.target.value }))} className="h-8 text-sm mt-1" data-testid={`rio-${field}`} />
                  </div>
                ))}
              </div>
              {[['session_feedback','Session Feedback (for low scores)'],['trainer_feedback','Trainer Feedback']].map(([field, label]) => (
                <div key={field}>
                  <Label className="text-xs">{label}</Label>
                  <Textarea value={outcomes[field]} onChange={e => setOutcomes(p => ({ ...p, [field]: e.target.value }))} rows={2} className="text-sm mt-1" data-testid={`rio-${field}`} />
                </div>
              ))}
              <div className="flex gap-2 pt-1">
                <Button size="sm" onClick={saveOutcomes} disabled={savingOutcomes} className="bg-lime text-lime-foreground hover:bg-lime/90">
                  {savingOutcomes ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Check className="w-3 h-3 mr-1" />}Save
                </Button>
                <Button size="sm" variant="outline" onClick={() => setOutcomesEdit(false)}>Cancel</Button>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
              {[
                { label: 'Attendance', value: event.total_attendance?.toLocaleString(), icon: Users },
                { label: 'Unique Partners', value: event.unique_partners?.toLocaleString(), icon: Users },
                { label: 'Feedback Score', value: event.feedback_score ? `${event.feedback_score}/5` : null, icon: Star },
              ].map(({ label, value, icon: Icon }) => (
                <div key={label} className="text-center p-3 bg-muted/30 rounded-lg">
                  <p className="text-xs text-muted-foreground">{label}</p>
                  <p className="text-xl font-bold mt-1">{value ?? '—'}</p>
                </div>
              ))}
              {event.session_feedback && (
                <div className="col-span-full">
                  <p className="text-xs text-muted-foreground font-medium mb-1">Session Feedback</p>
                  <p className="text-sm italic">"{event.session_feedback}"</p>
                </div>
              )}
              {event.trainer_feedback && (
                <div className="col-span-full">
                  <p className="text-xs text-muted-foreground font-medium mb-1">Trainer Feedback</p>
                  <p className="text-sm italic">"{event.trainer_feedback}"</p>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* POE Photos */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-base">POE Event Photos</CardTitle>
              <p className="text-xs text-muted-foreground mt-0.5">Proof of Execution photos for invoicing. Auto-deleted after 12 months.</p>
            </div>
            <Button size="sm" variant="outline" onClick={() => fileInputRef.current?.click()} className="h-8" data-testid="rio-upload-photo-btn">
              <Upload className="w-3.5 h-3.5 mr-1" />Upload
            </Button>
            <input ref={fileInputRef} type="file" accept="image/*" multiple className="hidden" onChange={handlePhotoUpload} />
          </div>
        </CardHeader>
        <CardContent className="pt-0">
          {photos.length === 0 ? (
            <div className="border-2 border-dashed rounded-lg p-8 text-center cursor-pointer hover:border-lime/50 transition-colors" onClick={() => fileInputRef.current?.click()}>
              <Image className="w-8 h-8 mx-auto mb-2 text-muted-foreground/40" />
              <p className="text-sm text-muted-foreground">Click to upload POE photos</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {photos.map(photo => (
                <div key={photo.id} className="relative group rounded-lg overflow-hidden aspect-video bg-muted">
                  <img src={regionalInPersonAPI.getPhotoUrl(photo.id)} alt={photo.original_filename} className="w-full h-full object-cover" onError={e => { e.target.style.display = 'none'; }} />
                  <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                    <Button size="sm" variant="destructive" className="h-7 text-xs" onClick={() => handleDeletePhoto(photo.id)}>
                      <Trash2 className="w-3 h-3 mr-1" />Remove
                    </Button>
                  </div>
                </div>
              ))}
              <div className="border-2 border-dashed rounded-lg aspect-video flex items-center justify-center cursor-pointer hover:border-lime/50 transition-colors" onClick={() => fileInputRef.current?.click()}>
                <Plus className="w-6 h-6 text-muted-foreground/40" />
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>

    {/* ── Report Modal ─────────────────────────────── */}
    <Dialog open={reportOpen} onOpenChange={setReportOpen}>
      <DialogContent className="max-w-4xl h-[90vh] flex flex-col">
        <DialogHeader className="flex-shrink-0 flex flex-row items-center justify-between">
          <DialogTitle className="flex items-center gap-2">
            <Mail className="w-5 h-5 text-lime" />Email-Ready Training Report
          </DialogTitle>
          {reportHtml && (
            <Button size="sm" onClick={downloadReportHtml} className="bg-lime text-lime-foreground hover:bg-lime/90 mr-6"
              data-testid="rio-download-report-btn">
              <Download className="w-4 h-4 mr-1.5" />Download HTML
            </Button>
          )}
        </DialogHeader>
        {reportLoading ? (
          <div className="flex-1 flex flex-col items-center justify-center gap-4">
            <div className="w-8 h-8 border-4 border-lime border-t-transparent rounded-full animate-spin" />
            <p className="text-sm text-muted-foreground">Generating AI synopsis and building report...</p>
          </div>
        ) : (
          <div className="flex-1 overflow-hidden rounded-lg border bg-white">
            <iframe
              srcDoc={reportHtml}
              title="Training Report Preview"
              className="w-full h-full"
              sandbox="allow-same-origin"
              data-testid="rio-report-preview"
            />
          </div>
        )}
      </DialogContent>
    </Dialog>
    <ContentItemLinkDialog open={contentItemDialogOpen} onOpenChange={setContentItemDialogOpen} onSelect={handleLinkContentItem} />
    </>
  );
};

export default RegionalInPersonDetailPage;
